import React, { useCallback, useEffect, useState } from "react";

import { Col, Container, Form, Row } from "react-bootstrap";

import { mapRegisterFromInput } from "..";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useInternalCreateParticipantMutation, useSubmitRegistrationMutation } from "@api/graphql";
import { getRegistrationType } from "@common/registration";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { FormErrorText } from "@components/form/FormErrorText";
import FormNavigationButtons from "@components/form/FormNavigationButtons";
import { FormStepProps } from "@components/form/MultiStepForm";
import { Loading } from "@components/loading";
import TermsAndConditionsModal from "@pages/register/TermsAndConditionsModal";
import { errorResolver } from "@pages/register/hooks/errorResolver";
import { useIsInternalUserCreatingAccount } from "@pages/register/hooks/useIsInternalUserCreatingAccount";
import { RegisterFormContextType, RegisterFormProvider } from "@pages/register/hooks/useRegisterFormContext";

import { ROUTES } from "../../../../routes";
import { displayParticipantTypes } from "../ParticipantOptions";
import { UserDetailsInput, UserInput } from "../RegisterFormTypes";

import { AccordionItem, accordionItems, accordionItemsType } from "./FormAccordion";

import styles from "./index.module.scss";

interface UserDetailsProps extends FormStepProps<UserInput, UserDetailsInput> {
  isInsertMode?: boolean;
  submitRef: React.RefObject<HTMLButtonElement>;
  isCreatedUserCompletingRegistration: boolean;
}

export const UserDetails = ({
  multiFormData,
  onCancel,
  isInsertMode = true,
  submitRef,
  isCreatedUserCompletingRegistration,
}: UserDetailsProps) => {
  const isInternalUserCreatingAccount = useIsInternalUserCreatingAccount();

  const userRoles = multiFormData.user_role;
  const participantType = multiFormData.participant_type;

  const {
    isResaleReporterOnly,
    isRoyaltyRecipientOnly,
    isArtist,
    isBeneficiary,
    isCollaboration,
    isArtCenter,
    isAmp,
    isCMO,
  } = getRegistrationType(userRoles, participantType);

  const [isJoiningExistingAccount, setIsJoiningExistingAccount] = useState(false);
  const [isAbnRegistered, setIsAbnRegistered] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [userDetailsInput, setUserDetailsInput] = useState<UserDetailsInput>({} as UserDetailsInput);

  const handleAbnRegistrationStatus = (status: boolean) => {
    setIsAbnRegistered(status);
  };

  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
  } = useForm<UserDetailsInput>({
    resolver: (values, options) =>
      errorResolver(
        values,
        {
          isAbnRegistered,
          isArtist,
          isBeneficiary,
          isCollaboration,
          isJoiningExistingAccount,
          isRoyaltyRecipientOnly,
          isAmp,
          isInternalUserCreatingAccount,
          isArtCenter,
          isCMO,
          isCreatedUserCompletingRegistration,
        },
        options,
      ),
    defaultValues: multiFormData.user_details,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [submitRegistration] = useSubmitRegistrationMutation({});
  const [internalCreateParticipant] = useInternalCreateParticipantMutation({});

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const submitFormData = useCallback(
    async (data: UserDetailsInput) => {
      try {
        if (isEmpty(data)) {
          setErrorMessage(
            "User details are incomplete or missing. Please fill in all required fields before submitting.",
          );
        } else {
          setLoading(true);
          const mappedInput = mapRegisterFromInput({ ...multiFormData, user_details: data });
          if (isInternalUserCreatingAccount) {
            const res = await internalCreateParticipant({
              variables: {
                input: {
                  ...mappedInput,
                },
              },
            });
            const participantId = res.data?.internalCreateParticipant?.participant_id;
            if (participantId) {
              navigate(ROUTES.Dashboard.NewAccounts.RegisterSuccess.path, { state: { participantId } });
            } else {
              setErrorMessage("Internal Creating Account failed. Please check your information and try again.");
            }
          } else {
            const res = await submitRegistration({
              variables: {
                input: {
                  ...mappedInput,
                },
              },
            });
            const participantId = res.data?.submitRegistration?.participant_id;
            if (participantId) {
              navigate(ROUTES.RegisterSubmission.RegisterSuccess.path, { state: { participantId } });
            } else {
              setErrorMessage("Registration failed. Please check your information and try again.");
            }
          }
        }
      } catch (error) {
        setErrorMessage("Something went wrong, please try again");
      } finally {
        setLoading(false);
      }
    },
    [internalCreateParticipant, isInternalUserCreatingAccount, multiFormData, navigate, submitRegistration],
  );

  useEffect(() => {
    if (isCreatedUserCompletingRegistration && isCMO) {
      submitFormData({
        ...multiFormData.user_details,
      });
    }
  }, [isCMO, isCreatedUserCompletingRegistration, multiFormData.user_details, submitFormData]);

  const handleFormSubmit = useCallback(
    async (data: UserDetailsInput) => {
      if (isValid) {
        setUserDetailsInput(data);
        if (isAbnRegistered && (isArtist || isBeneficiary)) {
          setErrorMessage("ABN is already registered for resale royalty.");
        } else if (!isJoiningExistingAccount && !isInternalUserCreatingAccount) {
          setShowModal(true);
        } else {
          await submitFormData(data);
        }
      } else {
        setErrorMessage("Please resolve all errors before submitting.");
      }
    },
    [
      isAbnRegistered,
      isArtist,
      isBeneficiary,
      isJoiningExistingAccount,
      isInternalUserCreatingAccount,
      isValid,
      submitFormData,
    ],
  );

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      setIsJoiningExistingAccount(checked);
      setValue("join_existing_participant", checked, { shouldValidate: true });
    },
    [setValue],
  );

  const filteredAccordionItems = useCallback(() => {
    return accordionItems(isCreatedUserCompletingRegistration).filter(({ key }: accordionItemsType) => {
      if (isResaleReporterOnly && key === "bank_details") {
        return false;
      }
      if (isRoyaltyRecipientOnly && key === "finance_contact") {
        return false;
      }
      if (isInternalUserCreatingAccount && (isAmp || isArtCenter || isCMO) && key === "contact_details") {
        return false;
      }
      if (isCreatedUserCompletingRegistration && (key === "account_details" || key === "bank_details")) {
        return false;
      }
      return isJoiningExistingAccount ? key === "contact_details" || key === "tax_details" : true;
    });
  }, [
    isAmp,
    isArtCenter,
    isCMO,
    isInternalUserCreatingAccount,
    isJoiningExistingAccount,
    isResaleReporterOnly,
    isRoyaltyRecipientOnly,
    isCreatedUserCompletingRegistration,
  ]);

  const participantTypes = displayParticipantTypes(multiFormData.participant_type);

  const formContextValue: RegisterFormContextType = {
    control,
    errors,
    multiFormData,
    watch,
    setValue,
    isJoiningExistingAccount,
    handleCheckboxChange,
    isAbnRegistered,
    handleAbnRegistrationStatus,
    ...getRegistrationType(userRoles, participantType),
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    submitFormData({
      ...multiFormData.user_details,
      ...userDetailsInput,
      terms_accepted: true,
    });
  }, [userDetailsInput, multiFormData, submitFormData]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {!isInternalUserCreatingAccount && <TermsAndConditionsModal isOpen={showModal} closeModal={handleCloseModal} />}
      <RegisterFormProvider value={formContextValue}>
        <Container>
          {userRoles.length === 0 || participantType.length === 0 ? (
            <Row className="mt-4 mb-4 text-center">
              <h2>Please complete the previous step before continuing.</h2>
            </Row>
          ) : (
            <>
              <Row>
                <Col>
                  <h3 className="mb-1 pb-3 text-center monoGrey1">Your details</h3>
                </Col>
              </Row>
              {participantTypes && (
                <h2 className={`${styles.participantTypes} b3 medium text-center`}>{participantTypes}</h2>
              )}
              <Row className={`${styles["mandatory-field"]} b4`}>*Mandatory field</Row>
              {filteredAccordionItems().map(({ key, header, Component, AdditionalProps }) => (
                <Row key={key}>
                  <CustomAccordion
                    accordionKey={key}
                    contrastOpenStyle={false}
                    header={<h5 className="mb-0 monoBlack">{header}</h5>}
                    defaultActiveKey={key}
                    customHeaderStyle={styles["accordion-header"]}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <AccordionItem Component={Component} AdditionalProps={AdditionalProps} />
                    </div>
                  </CustomAccordion>
                </Row>
              ))}

              <Form.Group>
                <Controller
                  name="receive_newsletter"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      className={classNames(styles["form-check"], "pt-0 pb-2 ps-4 mb-2")}
                      type="checkbox"
                      label={
                        <div className="b4 medium monoGray1 d-flex align-items-center h-100">
                          I’d like to receive the newsletter with resale royalty news and updates
                        </div>
                      }
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </Form.Group>
              <FormErrorText error={errors?.join_existing_participant} />
            </>
          )}
          <FormErrorText error={errorMessage} />
          <FormNavigationButtons
            isInsertMode={isInsertMode}
            onSubmit={handleFormSubmit}
            onCancel={handleCancel}
            handleSubmit={handleSubmit}
            submitRef={submitRef}
            showPreviousButton={!isCreatedUserCompletingRegistration}
          />
        </Container>
      </RegisterFormProvider>
    </>
  );
};
